import camelcaseKeys from 'camelcase-keys';
import queryString from 'query-string';

import type { GeofenceEvent, GeofenceImportData, ISOString } from '@cobliteam/common-types';
import type { Geofence, GeofenceFilter, GeofenceResume, GeofenceValidationQuery, PlainGeofence } from '@cobliteam/common-types/src/geofenceV3';
import type { UUID } from '@cobliteam/common-types/src/identifier';
import { type PageableContent, sortToParam } from '@cobliteam/common-types/src/pagination';

import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { Configuration, IRequest } from '../../request/Types';

class GeofenceRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getGeofences = async ({
    filters,
  }: {
    filters: GeofenceFilter;
  }): Promise<PageableContent<PlainGeofence>> => {
    const sort = filters.sort ? filters.sort.map(sortToParam) : undefined;
    const searchParams = queryString.stringify({ ...filters, sort });

    const requestData: IRequest = {
      endpoint: `/?${searchParams}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data, { deep: true });
  };

  public getGeofenceById = async (id: UUID): Promise<Geofence> => {
    const requestData: IRequest = {
      endpoint: `/${id}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data, { deep: true });
  };

  public getGeofenceResume = async ({ forceVisibility }: { forceVisibility?: boolean }): Promise<GeofenceResume[]> => {
    const searchParams = queryString.stringify({ forceVisibility }, { skipNull: true, skipEmptyString: true });

    const requestData: IRequest = {
      endpoint: `/resume?${searchParams}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data, { deep: true });
  };

  public getGeofenceForcedVisibility = async (): Promise<PlainGeofence[]> => {
    const requestData: IRequest = {
      endpoint: '/force-visibility',
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data, { deep: true });
  };

  public createGeofence = async (geofence: Omit<PlainGeofence, 'id'>): Promise<PlainGeofence> => {
    const { geometryPoint, geometryPolygon, forceVisibility, idIbgeEstado, idIbgeMunicipio, ...rest } = geofence;

    const requestData: IRequest = {
      endpoint: '/',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...rest,
        force_visibility: forceVisibility,
        geometry_point: geometryPoint,
        geometry_polygon: geometryPolygon,
        id_ibge_estado: idIbgeEstado,
        id_ibge_municipio: idIbgeMunicipio,
      }),
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data, { deep: true });
  };

  public updateGeofence = async (geofence: PlainGeofence): Promise<PlainGeofence> => {
    const { geometryPoint, geometryPolygon, forceVisibility, idIbgeEstado, idIbgeMunicipio, ...rest } = geofence;

    const requestData: IRequest = {
      endpoint: `/${geofence.id}`,
      method: HttpMethod.PUT,
      body: JSON.stringify({
        ...rest,
        force_visibility: forceVisibility,
        id_ibge_estado: idIbgeEstado,
        id_ibge_municipio: idIbgeMunicipio,
        geometry_point: geometryPoint,
        geometry_polygon: geometryPolygon,
      }),
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data, { deep: true });
  };

  public deleteGeofence = async (id: UUID): Promise<null> => {
    const requestData: IRequest = {
      endpoint: `/${id}`,
      method: HttpMethod.DELETE,
    };

    await this.requester.request(requestData);

    return null;
  };

  public validateImportGeofences = async (validationQueries: GeofenceValidationQuery[]): Promise<GeofenceImportData> => {
    const requestData: IRequest = {
      endpoint: '/import',
      method: HttpMethod.POST,
      body: JSON.stringify(validationQueries),
    };

    const geofenceAPI = await this.requester.request(requestData);

    return camelcaseKeys(geofenceAPI.data);
  };

  public importGeofences = async (importId: UUID): Promise<null> => {
    const requestData: IRequest = {
      endpoint: `/import/${importId}/confirm`,
      method: HttpMethod.POST,
    };

    await this.requester.request(requestData);

    return null;
  };

  public getGeofenceEvents = async (startTime: ISOString, endTime: ISOString): Promise<GeofenceEvent[]> => {
    const requestData: IRequest = {
      endpoint: `/events?startTime=${startTime}&endTime=${endTime}`,
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);
    return camelcaseKeys(data, { deep: true });
  };
}

export default GeofenceRequester;
